import { Controller } from "@hotwired/stimulus"

// Generic collapsible banner: a banner-styled header that toggles an animated disclosure body.
// Drives the shared/components/collapsible_banner partial. Both start CLOSED by default. Toggling
// `.expanded` on the `wrap` target animates its height via a CSS grid-template-rows transition
// (0fr <-> 1fr) — see .cc-disclosure-wrap in application.tailwind.css — so it glides rather than jumps.
// A `chevron` target rotates; an `icon`/`iconExpanded` pair (the + / x variant) swaps. While collapsed
// the wrap is marked `inert` so its descendants stay out of the keyboard tab order and a11y tree.
//
// `closeOnSuccess` is an optional hook for a form embedded in the body: wire
// `data-action="turbo:submit-end->collapsible-banner#closeOnSuccess"` and the banner animates itself
// shut after a successful submit, WITHOUT the body being re-rendered — so any typed input is preserved.
export default class extends Controller {
  static targets = ["wrap", "chevron", "icon", "iconExpanded", "control", "autofocus"]

  connect() {
    this.expanded = this.hasWrapTarget && this.wrapTarget.classList.contains("expanded")
    this.reflect()
    // When rendered already-open (e.g. arrived via a deep link that expands the banner), move focus into
    // the body so keyboard users land where they came to act (the note textarea).
    if (this.expanded && this.hasAutofocusTarget) this.autofocusTarget.focus()
  }

  toggle() {
    this.setExpanded(!this.expanded)
  }

  open() {
    this.setExpanded(true)
  }

  close() {
    this.setExpanded(false)
  }

  // Only collapse on a successful submit (e.g. the note form's turbo:submit-end).
  closeOnSuccess(event) {
    if (event?.detail?.success) this.setExpanded(false)
  }

  setExpanded(expanded) {
    this.expanded = expanded
    this.reflect()
  }

  reflect() {
    if (this.hasWrapTarget) {
      this.wrapTarget.classList.toggle("expanded", this.expanded)
      // The wrap collapses to a 0fr grid row (visually hidden) but its descendants stay in the tab order
      // and a11y tree, so mark it inert while closed to keep keyboard/AT users out of hidden controls.
      this.wrapTarget.inert = !this.expanded
    }
    if (this.hasChevronTarget) this.chevronTarget.classList.toggle("rotate-180", this.expanded)
    if (this.hasIconTarget) this.iconTarget.classList.toggle("hidden", this.expanded)
    if (this.hasIconExpandedTarget) this.iconExpandedTarget.classList.toggle("hidden", !this.expanded)
    // aria-expanded belongs on the interactive header (the role=button control) when present.
    const ariaHost = this.hasControlTarget ? this.controlTarget : this.element
    ariaHost.setAttribute("aria-expanded", this.expanded ? "true" : "false")
  }
}
